import { PW_AUTH_TOKEN_KEY } from "@pathwright/auth/src/api/client"
import { handleAuthToken } from "@pathwright/auth/src/lib/utils/auth-token"
import {
  RefreshTokenMutationVariables,
  useRefreshTokenMutation
} from "@pathwright/graphql-client/src/generated"
import {
  clearInvalidToken,
  isUserProxied,
  parseAuthToken
} from "@pathwright/utils/src/token/utils"
import { useCallback, useEffect } from "react"
import { ZodError } from "zod"
import { getEnv, getIsLocalHost } from "../../lib/env"
import { getSchoolUrl } from "../../utils/urls"
import { useLastHeartbeatVar } from "../heartbeat/heartbeat"

// useRefreshToken's main concern is to not allow the user's auth token to expire.
// It accomplishes this by refreshing the user's auth token when it's past half life,
// piggy backing on the heartbeat mechanism for determining when to refresh.
// A token scoped to another school is not treated as invalid: in development
// the GraphQL server reissues it for the request school.

// The refresh mutation calls Django server-to-server, so its Set-Cookie never
// reaches the browser. On the school origin, call that endpoint directly: the
// same response reissues the JWT and extends cbsessionid. Localhost and tests
// talk to a remote school, where that cookie would be third-party.
function shouldRefreshOnSchoolOrigin() {
  return !getIsLocalHost() && !getEnv("test")
}

async function refreshTokenOnSchoolOrigin(authToken: string) {
  const url = new URL(getSchoolUrl("/api/private/jwt/auth/refresh-token/"))

  const response = await fetch(url, {
    method: "GET",
    credentials: "same-origin",
    headers: {
      Authorization: `Token ${authToken}`,
      Accept: "application/json"
    }
  })
  if (!response.ok) {
    throw new Error("Failed to refresh token.")
  }
  const data = await response.json()
  return typeof data?.token === "string" ? data.token : null
}

const useRefreshToken = () => {
  const lastHeartbeat = useLastHeartbeatVar()

  const [refreshTokenMutation] = useRefreshTokenMutation()

  const refreshToken = useCallback(
    async (variables?: RefreshTokenMutationVariables) => {
      const oldAuthToken = localStorage.getItem(PW_AUTH_TOKEN_KEY)
      const newAuthToken =
        oldAuthToken && shouldRefreshOnSchoolOrigin()
          ? await refreshTokenOnSchoolOrigin(oldAuthToken)
          : (await refreshTokenMutation({ variables })).data?.refreshToken

      if (oldAuthToken && newAuthToken) {
        // If the new auth token is for a different school, we need to refetch queries.
        if (
          parseAuthToken(oldAuthToken).iss !== parseAuthToken(newAuthToken).iss
        ) {
          // Note: this will refetch queries.
          handleAuthToken(newAuthToken)
        } else {
          // Note: quietly storing the new auth token, no need to refetch queries.
          localStorage.setItem(PW_AUTH_TOKEN_KEY, newAuthToken)
        }
      } else {
        clearInvalidToken()
      }
    },
    [refreshTokenMutation]
  )

  const handleInvalidToken = useCallback(async () => {
    if (isUserProxied()) return

    try {
      parseAuthToken()
    } catch {
      clearInvalidToken()
    }
  }, [])

  const handleRefreshToken = useCallback(async () => {
    if (isUserProxied()) return

    try {
      parseAuthToken()
      await refreshToken()
    } catch (err) {
      if (!(err instanceof ZodError)) {
        console.error(`Unexpected Error: Failed to refresh token.`, err)
      }
      clearInvalidToken()
    }
  }, [refreshToken])

  const attemptRefresh = useCallback(async () => {
    // Proxy JWTs are not refreshed via refreshToken — they are re-minted with
    // proxyAuthToken. Attempting a normal refresh fails with "Permission denied"
    // and clearInvalidToken would tear down the canvas session on reload.
    if (isUserProxied()) return

    await handleInvalidToken()

    if (!localStorage.getItem(PW_AUTH_TOKEN_KEY)) return

    const { iat: tokenIssuedAtDate, exp: tokenExpirationDate } =
      parseAuthToken()
    if (!tokenIssuedAtDate || !tokenExpirationDate) return

    const diff = tokenExpirationDate.getTime() - tokenIssuedAtDate.getTime()
    const halfLife = diff / 2

    const refreshTime = Math.min(
      tokenIssuedAtDate.getTime() + halfLife,
      tokenIssuedAtDate.getTime() + 1000 * 60 * 60 * 8 // 8 hours
    )
    // Refresh token if it's past half life.
    const shouldRefresh = refreshTime < new Date().getTime()

    if (shouldRefresh) handleRefreshToken()
  }, [handleInvalidToken, handleRefreshToken])

  useEffect(() => {
    attemptRefresh()
  }, [attemptRefresh, lastHeartbeat])
}

export function RefreshToken() {
  useRefreshToken()
  return null
}

RefreshToken.displayName = "RefreshToken"

export default useRefreshToken
